import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import FormModal from "../../../components/form/FormModal";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";
import FormTextarea from "../../../components/form/FormTextarea";

import {
  getGroundSlotsForDate,
  listVenuesBasic,
  searchUsersClient,
} from "../Reservations/reservationsApi";
import { listVenueGrounds as listVenueGroundsByVenue } from "../Venues/venueGroundsApi";
import { listGames } from "../Games/gameApi";

const todayYMD = () => new Date().toISOString().slice(0, 10);

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm || "00:00")
    .split(":")
    .map((x) => parseInt(x, 10) || 0);

  return h * 60 + m;
};

const to12Label = (startHH, endHH) => {
  const fmt = (HH) => {
    const h = parseInt(HH, 10);
    const mer = h >= 12 ? "PM" : "AM";
    let hh = h % 12;
    if (hh === 0) hh = 12;
    return `${String(hh).padStart(2, "0")}:00 ${mer}`;
  };

  return `${fmt(startHH)} – ${fmt(endHH)}`;
};

const getDateInfo = (isoDate) => {
  if (!isoDate) return "";

  const selected = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(selected.getTime())) return "";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDay = new Date(selected);
  selectedDay.setHours(0, 0, 0, 0);

  const diffDays = Math.round((selectedDay - today) / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";

  return selected.toLocaleDateString("en-IN", { weekday: "long" });
};

const emptyForm = {
  hostUserId: "",
  hostUserName: "",
  venueId: "",
  venueGroundId: "",
  sportId: "",
  date: todayYMD(),
  selectedSlot: null,
  totalPlayers: "10",
  creatorPlayersCount: "1",
  skillLevel: "Intermediate",
  matchType: "friendly",
  pricePerPlayer: "0",
  notes: "",
};

const LocalGameForm = ({ open, onCancel, onSubmit }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const [venues, setVenues] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [sports, setSports] = useState([]);
  const [slots, setSlots] = useState([]);

  const [loadingVenues, setLoadingVenues] = useState(false);
  const [loadingGrounds, setLoadingGrounds] = useState(false);
  const [loadingSports, setLoadingSports] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [userQ, setUserQ] = useState("");
  const [userDrop, setUserDrop] = useState([]);
  const [showUserDrop, setShowUserDrop] = useState(false);

  const selectedVenue = useMemo(
    () => venues.find((v) => String(v._id || v.id) === String(form.venueId)),
    [venues, form.venueId]
  );

  const dateInfo = useMemo(() => getDateInfo(form.date), [form.date]);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));

    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;

    setForm(emptyForm);
    setErrors({});
    setUserQ("");
    setUserDrop([]);
    setShowUserDrop(false);
    setSlots([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let alive = true;

    const run = async () => {
      setLoadingVenues(true);
      setLoadingSports(true);

      try {
        const [venueItems, sportItems] = await Promise.all([
          listVenuesBasic({ pageSize: 300 }),
          listGames(),
        ]);

        if (!alive) return;

        setVenues(Array.isArray(venueItems) ? venueItems : []);
        setSports(Array.isArray(sportItems) ? sportItems : []);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to load form data");
      } finally {
        if (alive) {
          setLoadingVenues(false);
          setLoadingSports(false);
        }
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const q = String(userQ || "").trim();

      if (q.length < 2 || form.hostUserId) {
        if (alive) setUserDrop([]);
        return;
      }

      try {
        const users = await searchUsersClient(q);
        if (alive) setUserDrop((users || []).slice(0, 12));
      } catch {
        if (alive) setUserDrop([]);
      }
    };

    const timer = setTimeout(run, 220);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [userQ, form.hostUserId]);

  useEffect(() => {
    if (!form.venueId) {
      setGrounds([]);
      set("venueGroundId", "");
      setSlots([]);
      return;
    }

    let alive = true;

    const run = async () => {
      setLoadingGrounds(true);

      try {
        const data = await listVenueGroundsByVenue(form.venueId, {
          includeInactive: false,
          pageSize: 300,
        });

        if (!alive) return;

        setGrounds(Array.isArray(data) ? data : []);
        setForm((prev) => ({
          ...prev,
          venueGroundId: "",
          selectedSlot: null,
        }));
        setSlots([]);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to load grounds");
      } finally {
        if (alive) setLoadingGrounds(false);
      }
    };

    run();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.venueId]);

  const refreshSlots = useCallback(async () => {
    if (!form.venueId || !form.venueGroundId || !form.date) {
      setSlots([]);
      return;
    }

    const slug = selectedVenue?.slug;

    if (!slug) {
      setSlots([]);
      return;
    }

    setLoadingSlots(true);

    try {
      const resp = await getGroundSlotsForDate(slug, form.venueGroundId, form.date, 60);
      const apiSlots = Array.isArray(resp?.slots) ? resp.slots : [];

      const now = new Date();
      const isToday = new Date(form.date).toDateString() === now.toDateString();
      const nowMin = now.getHours() * 60 + now.getMinutes();

      const mapped = apiSlots.map((slot) => {
        const start = slot.start;
        const end = slot.end;

        const past = isToday && toMinutes(end) <= nowMin;
        const status = slot.available ? "available" : slot.status || "booked";
        const label12 = to12Label(start.slice(0, 2), end.slice(0, 2));

        return {
          start,
          end,
          label12,
          status,
          past,
          price: Number(slot?.discountedPrice ?? slot?.price ?? 0),
          currency: slot.currency || "",
        };
      });

      setSlots(mapped);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [form.venueId, form.venueGroundId, form.date, selectedVenue]);

  useEffect(() => {
    refreshSlots();
  }, [refreshSlots]);

  const validate = () => {
    const e = {};

    if (!form.hostUserId) e.hostUserId = "Host user is required";
    if (!form.venueId) e.venueId = "Venue is required";
    if (!form.venueGroundId) e.venueGroundId = "Ground is required";
    if (!form.sportId) e.sportId = "Sport is required";
    if (!form.date) e.date = "Date is required";
    if (!form.selectedSlot) e.selectedSlot = "Select one available slot";

    const totalPlayers = Number(form.totalPlayers);
    const creatorPlayersCount = Number(form.creatorPlayersCount);

    if (!Number.isInteger(totalPlayers) || totalPlayers < 2) {
      e.totalPlayers = "Total players must be at least 2";
    }

    if (!Number.isInteger(creatorPlayersCount) || creatorPlayersCount < 1) {
      e.creatorPlayersCount = "Creator players count must be at least 1";
    }

    if (
      Number.isInteger(totalPlayers) &&
      Number.isInteger(creatorPlayersCount) &&
      creatorPlayersCount > totalPlayers
    ) {
      e.creatorPlayersCount = "Creator players cannot exceed total players";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    if (!validate()) {
      toast.error("Please fix validation errors");
      return;
    }

    const payload = {
      hostUserId: form.hostUserId,
      venueId: form.venueId,
      venueGroundId: form.venueGroundId,
      sportId: form.sportId,
      date: form.date,
      startTime: form.selectedSlot.start,
      endTime: form.selectedSlot.end,
      totalPlayers: Number(form.totalPlayers),
      creatorPlayersCount: Number(form.creatorPlayersCount),
      skillLevel: form.skillLevel,
      matchType: form.matchType,
      pricePerPlayer: Number(form.pricePerPlayer || 0),
      notes: String(form.notes || "").trim(),
    };

    try {
      setSubmitting(true);
      await onSubmit?.(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onCancel?.();
  };

  return (
    <FormModal
      open={open}
      title="Create Local Game"
      width="900px"
      submitting={submitting}
      submitText="Create Local Game"
      submittingText="Creating..."
      cancelText="Cancel"
      onClose={handleClose}
      onSubmit={submit}
    >
      <div className="lg-create-form">
        <div className="lg-create-grid">
          <FormField
            label="Host / Creator"
            error={errors.hostUserId}
            hint="This user will become the game creator and first participant."
            className="lg-create-span-2"
          >
            <div className="lg-user-combobox">
              <FormInput
                placeholder="Search by name, phone, or email"
                value={form.hostUserId ? form.hostUserName || userQ : userQ}
                onChange={(event) => {
                  setForm((prev) => ({
                    ...prev,
                    hostUserId: "",
                    hostUserName: "",
                  }));
                  setUserQ(event.target.value);
                  setShowUserDrop(true);
                }}
                onFocus={() => {
                  if (!form.hostUserId) setShowUserDrop(true);
                }}
                onBlur={() => setTimeout(() => setShowUserDrop(false), 130)}
              />

              {showUserDrop && !form.hostUserId && (
                <div
                  className="lg-user-dropdown"
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {userQ.trim().length < 2 ? (
                    <div className="lg-user-option is-hint">
                      Type at least 2 characters…
                    </div>
                  ) : userDrop.length === 0 ? (
                    <div className="lg-user-option is-hint">
                      Searching / no matches…
                    </div>
                  ) : (
                    userDrop.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="lg-user-option"
                        onMouseDown={() => {
                          setForm((prev) => ({
                            ...prev,
                            hostUserId: user.id,
                            hostUserName: user.name || "",
                          }));

                          setUserQ(
                            [user.name, user.phone || user.phoneE164, user.email]
                              .filter(Boolean)
                              .join(" • ")
                          );
                          setShowUserDrop(false);
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.hostUserId;
                            return next;
                          });
                        }}
                      >
                        <span>{user.name || "—"}</span>
                        <small>
                          {[user.phone || user.phoneE164, user.email]
                            .filter(Boolean)
                            .join(" • ")}
                        </small>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </FormField>

          <FormField label="Venue" error={errors.venueId}>
            <FormSelect
              value={form.venueId}
              onChange={(event) => {
                set("venueId", event.target.value);
                setForm((prev) => ({
                  ...prev,
                  venueGroundId: "",
                  selectedSlot: null,
                }));
              }}
              required
            >
              <option value="">
                {loadingVenues ? "Loading venues…" : "Select venue…"}
              </option>

              {venues.map((venue) => (
                <option key={venue._id || venue.id} value={venue._id || venue.id}>
                  {venue.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Ground" error={errors.venueGroundId}>
            <FormSelect
              value={form.venueGroundId}
              onChange={(event) => {
                set("venueGroundId", event.target.value);
                setForm((prev) => ({
                  ...prev,
                  selectedSlot: null,
                }));
              }}
              disabled={!form.venueId || loadingGrounds || grounds.length === 0}
              required
            >
              <option value="">
                {loadingGrounds
                  ? "Loading grounds…"
                  : grounds.length
                    ? "Select ground…"
                    : "No active grounds"}
              </option>

              {grounds.map((ground) => (
                <option key={ground._id || ground.id} value={ground._id || ground.id}>
                  {ground.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Sport" error={errors.sportId}>
            <FormSelect
              value={form.sportId}
              onChange={(event) => set("sportId", event.target.value)}
              disabled={loadingSports}
              required
            >
              <option value="">
                {loadingSports ? "Loading sports…" : "Select sport…"}
              </option>

              {sports.map((sport) => (
                <option key={sport._id || sport.id} value={sport._id || sport.id}>
                  {sport.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Date" error={errors.date}>
            <FormInput
              type="date"
              value={form.date}
              min={todayYMD()}
              onChange={(event) => {
                const nextDate = event.target.value;

                if (nextDate && nextDate < todayYMD()) {
                  toast.error("Past dates cannot be selected");
                  return;
                }

                set("date", nextDate);
                setForm((prev) => ({
                  ...prev,
                  selectedSlot: null,
                }));
              }}
              required
            />

            {dateInfo ? <small className="lg-create-note">{dateInfo}</small> : null}
          </FormField>

          <FormField
            label="Available Slot"
            error={errors.selectedSlot}
            className="lg-create-span-2"
          >
            {!form.venueGroundId || !form.date ? (
              <div className="lg-create-note">Select venue, ground, and date first.</div>
            ) : loadingSlots ? (
              <div className="lg-create-note">Loading slots…</div>
            ) : slots.length === 0 ? (
              <div className="lg-create-note">No slots generated.</div>
            ) : (
              <div className="lg-create-slots">
                {slots.map((slot) => {
                  const selected =
                    form.selectedSlot?.start === slot.start &&
                    form.selectedSlot?.end === slot.end;

                  let tone = "default";
                  if (selected) tone = "success";
                  else if (slot.past) tone = "muted";
                  else if (slot.status !== "available") tone = "danger";

                  return (
                    <button
                      key={`${slot.start}-${slot.end}`}
                      type="button"
                      className={[
                        "sa-chip",
                        "sa-chip--slot",
                        `sa-chip--${tone}`,
                        selected ? "sa-chip--selected" : "",
                      ].join(" ")}
                      disabled={slot.status !== "available" || slot.past}
                      onClick={() => {
                        set("selectedSlot", slot);

                        if (slot.price > 0) {
                          setForm((prev) => ({
                            ...prev,
                            selectedSlot: slot,
                            pricePerPlayer: String(slot.price),
                          }));
                        }
                      }}
                      title={
                        selected
                          ? "Selected"
                          : slot.past
                            ? "Past time"
                            : slot.status !== "available"
                              ? "Booked / unavailable"
                              : "Click to select"
                      }
                    >
                      {slot.label12}
                      {slot.price ? ` • ${slot.price} ${slot.currency || ""}` : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </FormField>

          <FormField label="Total Players" error={errors.totalPlayers}>
            <FormInput
              type="number"
              min="2"
              value={form.totalPlayers}
              onChange={(event) => set("totalPlayers", event.target.value)}
              required
            />
          </FormField>

          <FormField label="Creator Joining With" error={errors.creatorPlayersCount}>
            <FormInput
              type="number"
              min="1"
              value={form.creatorPlayersCount}
              onChange={(event) => set("creatorPlayersCount", event.target.value)}
              required
            />
          </FormField>

          <FormField label="Skill Level">
            <FormSelect
              value={form.skillLevel}
              onChange={(event) => set("skillLevel", event.target.value)}
            >
              <option value="Casual">Casual</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Competitive">Competitive</option>
            </FormSelect>
          </FormField>

          <FormField label="Match Type">
            <FormSelect
              value={form.matchType}
              onChange={(event) => set("matchType", event.target.value)}
            >
              <option value="friendly">Friendly</option>
              <option value="competitive">Competitive</option>
            </FormSelect>
          </FormField>

          <FormField label="Price Per Player">
            <FormInput
              type="number"
              min="0"
              value={form.pricePerPlayer}
              onChange={(event) => set("pricePerPlayer", event.target.value)}
              placeholder="0"
            />
          </FormField>

          <FormField label="Notes" className="lg-create-span-2">
            <FormTextarea
              rows={4}
              value={form.notes}
              onChange={(event) => set("notes", event.target.value)}
              placeholder="Rules, equipment, dress code, or match instructions..."
            />
          </FormField>
        </div>
      </div>
    </FormModal>
  );
};

export default LocalGameForm;